import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Modal from '../components/Modal';
import ContactForm from '../components/ContactForm';
import BulkDeleteModal from '../components/BulkDeleteModal';
import { Contact } from '../types/Message';
import { Trash2 } from 'lucide-react';

interface Lead {
  id: number;
  name: string;
}

const Contacts = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [selectedContacts, setSelectedContacts] = useState<Set<number>>(new Set());
  const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false);

  useEffect(() => {
    loadContacts();
    loadLeads();
  }, []);

  const loadContacts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/messaging/contacts/');
      const data = response.data.results || response.data;
      setContacts(data);
    } catch (error) {
      console.error('Error loading contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLeads = async () => {
    try {
      const response = await api.get('/leads/leads/');
      const data = response.data.results || response.data;
      setLeads(data);
    } catch (error) {
      console.error('Error loading leads:', error);
    }
  };

  const handleSaveContact = async (data: Partial<Contact>) => {
    if (selectedContact) {
      await api.put(`/messaging/contacts/${selectedContact.id}/`, data);
    } else {
      await api.post('/messaging/contacts/', data);
    }
    setShowForm(false);
    setSelectedContact(null);
    loadContacts();
  };

  const handleEdit = (contact: Contact) => {
    setSelectedContact(contact);
    setShowForm(true);
  };

  const handleDelete = async (contact: Contact) => {
    if (window.confirm('Tem certeza que deseja excluir este contato?')) {
      await api.delete(`/messaging/contacts/${contact.id}/`);
      loadContacts();
    }
  };

  const handleAddNew = () => {
    setSelectedContact(null);
    setShowForm(true);
  };

  const toggleSelectContact = (id: number) => {
    setSelectedContacts(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const allIds = filteredContacts.map(c => c.id);
    const allSelected = allIds.every(id => selectedContacts.has(id));
    setSelectedContacts(prev => {
      const next = new Set(prev);
      allIds.forEach(id => allSelected ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const handleBulkDeleteComplete = () => {
    setIsBulkDeleteOpen(false);
    setSelectedContacts(new Set());
    loadContacts();
  };

  const filteredContacts = contacts.filter(contact =>
    contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.phone?.includes(searchTerm)
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)', animation: 'fadeIn 0.3s ease-out' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)' }}>
            Gerencie seus contatos e informações de comunicação
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
          {filteredContacts.length > 0 && (
            <button
              onClick={toggleSelectAll}
              className="glass-btn"
              style={{
                background: 'var(--glass-bg)',
                backdropFilter: 'var(--glass-blur)',
                border: '1px solid var(--glass-border)',
                padding: 'var(--space-2-5) var(--space-4)',
                borderRadius: 'var(--radius-xl)',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                transition: 'all var(--transition-fast)',
              }}
            >
              {filteredContacts.every(c => selectedContacts.has(c.id)) ? 'Desmarcar tudo' : 'Selecionar tudo'}
            </button>
          )}
          <button
            onClick={handleAddNew}
            style={{
              background: 'var(--bg-gradient)',
              border: 'none',
              padding: 'var(--space-2-5) var(--space-4)',
              borderRadius: 'var(--radius-xl)',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--text-inverted)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              boxShadow: 'var(--shadow-primary)',
              transition: 'all var(--transition-fast)',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Novo Contato
          </button>
        </div>
      </div>

      {/* Search */}
      <div style={{
        background: 'var(--glass-bg-strong)',
        backdropFilter: 'var(--glass-blur)',
        borderRadius: 'var(--radius-2xl)',
        border: '1px solid var(--glass-border)',
        padding: 'var(--space-4)',
      }}>
        <div style={{ position: 'relative' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" strokeWidth="2" style={{ position: 'absolute', left: 'var(--space-3)', top: '50%', transform: 'translateY(-50%)' }}>
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            type="text"
            placeholder="Buscar contatos por nome, email ou telefone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
            style={{
              width: '100%',
              padding: 'var(--space-2-5) var(--space-3) var(--space-2-5) var(--space-10)',
              fontSize: 'var(--text-sm)',
              background: 'var(--bg-primary)',
              border: '1px solid var(--border-light)',
              borderRadius: 'var(--radius-xl)',
              color: 'var(--text-primary)',
              transition: 'all var(--transition-fast)',
              outline: 'none',
            }}
          />
        </div>
      </div>

      {/* Contacts Grid */}
      {loading ? (
        <div style={{ padding: 'var(--space-16)', textAlign: 'center' }}>
          <div style={{ width: '48px', height: '48px', border: '3px solid var(--border-light)', borderTopColor: 'var(--color-primary-600)', borderRadius: '50%', animation: 'spin 0.6s linear infinite', margin: '0 auto var(--space-4)' }} />
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>Carregando contatos...</p>
        </div>
      ) : filteredContacts.length === 0 ? (
        <div style={{
          background: 'var(--glass-bg-strong)',
          backdropFilter: 'var(--glass-blur)',
          borderRadius: 'var(--radius-2xl)',
          border: '1px solid var(--glass-border)',
          padding: 'var(--space-16)',
          textAlign: 'center',
        }}>
          <div style={{ width: '80px', height: '80px', borderRadius: 'var(--radius-full)', background: 'var(--color-primary-50)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto var(--space-5)' }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary-400)" strokeWidth="1.5">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
          </div>
          <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}>
            {searchTerm ? 'Nenhum contato encontrado' : 'Nenhum contato cadastrado'}
          </h3>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)' }}>
            {searchTerm ? 'Tente outra busca' : 'Adicione seu primeiro contato'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 'var(--space-4)' }}>
          {filteredContacts.map((contact) => (
            <div
              key={contact.id}
              style={{
                background: 'var(--glass-bg-strong)',
                backdropFilter: 'var(--glass-blur)',
                borderRadius: 'var(--radius-2xl)',
                border: '1px solid var(--glass-border)',
                padding: 'var(--space-5)',
                transition: 'all var(--transition-fast)',
              }}
              className="contact-card"
            >
              {/* Avatar e Nome */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
                <input
                  type="checkbox"
                  checked={selectedContacts.has(contact.id)}
                  onChange={() => toggleSelectContact(contact.id)}
                  style={{ width: '18px', height: '18px', accentColor: 'var(--color-primary-600)', cursor: 'pointer', flexShrink: 0 }}
                />
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: 'var(--radius-full)',
                  background: 'var(--bg-gradient)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'var(--font-weight-semibold)',
                  fontSize: 'var(--text-lg)',
                  flexShrink: 0,
                  boxShadow: 'var(--shadow-primary)',
                }}>
                  {contact.avatar ? (
                    <img src={contact.avatar} alt={contact.name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                  ) : (
                    contact.name.charAt(0).toUpperCase()
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-primary)', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {contact.name}
                  </h3>
                  {contact.lead_name && (
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-primary-600)', background: 'var(--color-primary-50)', padding: '2px 8px', borderRadius: 'var(--radius-full)' }}>
                      {contact.lead_name}
                    </span>
                  )}
                </div>
              </div>

              {/* Canais de contato */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', marginBottom: 'var(--space-4)' }}>
                {contact.phone && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                    </svg>
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>{contact.display_phone || contact.phone}</span>
                  </div>
                )}
                {contact.whatsapp_number && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="#25D366" stroke="none">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>{contact.whatsapp_number}</span>
                  </div>
                )}
                {contact.email && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{contact.email}</span>
                  </div>
                )}
                {contact.instagram_handle && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#E1306C" strokeWidth="2">
                      <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                    </svg>
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>@{contact.instagram_handle}</span>
                  </div>
                )}
              </div>

              {/* Última interação */}
              {contact.last_interaction && (
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', marginBottom: 'var(--space-4)' }}>
                  Última interação: {new Date(contact.last_interaction).toLocaleDateString('pt-BR')}
                </p>
              )}

              {/* Ações */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
                <button
                  onClick={() => handleEdit(contact)}
                  className="action-btn"
                  style={{
                    padding: 'var(--space-2)',
                    background: 'var(--glass-bg-subtle)',
                    border: '1px solid var(--glass-border)',
                    borderRadius: 'var(--radius-lg)',
                    cursor: 'pointer',
                    color: 'var(--color-primary-600)',
                    transition: 'all var(--transition-fast)',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </button>
                <button
                  onClick={() => handleDelete(contact)}
                  className="action-btn"
                  style={{
                    padding: 'var(--space-2)',
                    background: 'var(--glass-bg-subtle)',
                    border: '1px solid var(--glass-border)',
                    borderRadius: 'var(--radius-lg)',
                    cursor: 'pointer',
                    color: 'var(--color-error-600)',
                    transition: 'all var(--transition-fast)',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6"/>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Floating Bulk Action Bar */}
      {selectedContacts.size > 0 && (
        <div style={{
          position: 'fixed',
          bottom: 'var(--space-6)',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'var(--glass-bg-strong)',
          backdropFilter: 'blur(20px)',
          border: '1px solid var(--glass-border-strong)',
          borderRadius: 'var(--radius-2xl)',
          padding: 'var(--space-3) var(--space-5)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-4)',
          boxShadow: 'var(--shadow-2xl)',
          zIndex: 50,
          animation: 'slideUp 0.3s ease-out',
        }}>
          <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-primary)' }}>
            {selectedContacts.size} {selectedContacts.size === 1 ? 'contato selecionado' : 'contatos selecionados'}
          </span>
          <div style={{ width: '1px', height: '24px', background: 'var(--border-light)' }} />
          <button
            onClick={() => setIsBulkDeleteOpen(true)}
            style={{
              padding: 'var(--space-2) var(--space-4)',
              background: '#ef4444',
              border: 'none',
              borderRadius: 'var(--radius-xl)',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              transition: 'all 0.2s',
            }}
          >
            <Trash2 size={16} />
            Excluir
          </button>
          <button
            onClick={() => setSelectedContacts(new Set())}
            style={{
              padding: 'var(--space-2) var(--space-4)',
              background: 'var(--glass-bg)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-xl)',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            Cancelar
          </button>
        </div>
      )}

      {/* Bulk Delete Modal */}
      <BulkDeleteModal
        isOpen={isBulkDeleteOpen}
        onClose={() => setIsBulkDeleteOpen(false)}
        onConfirm={handleBulkDeleteComplete}
        type="contacts"
        selectedIds={Array.from(selectedContacts)}
      />

      {/* Modal de formulário */}
      <Modal
        isOpen={showForm}
        onClose={() => { setShowForm(false); setSelectedContact(null); }}
        title={selectedContact ? 'Editar Contato' : 'Novo Contato'}
        size="lg"
      >
        <ContactForm
          contact={selectedContact}
          leads={leads}
          onSave={handleSaveContact}
          onCancel={() => { setShowForm(false); setSelectedContact(null); }}
        />
      </Modal>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .contact-card:hover { transform: translateY(-2px); box-shadow: var(--shadow-lg); }
        .action-btn:hover { background: var(--glass-bg) !important; transform: scale(1.05); }
        .search-input:focus { border-color: var(--color-primary-300) !important; box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1) !important; }
      `}</style>
    </div>
  );
};

export default Contacts;
